// src/frontend/hooks/usePagination.ts
import { useEffect, useState, useCallback } from "react";
import type { TablePaginationConfig } from "antd/es/table";
import { Message } from "@/frontend/components/common";
import type {
    ApiResponse,
    PaginationRequestDTO,
    PaginationResponseDTO
} from "@/shared/dto";

interface UsePaginationOptions<T> {
    fetchFn: (params?: PaginationRequestDTO) => Promise<ApiResponse<PaginationResponseDTO<T>>>;
    errorMessage?: string;
    initialParams?: PaginationRequestDTO;
    initialPagination?: TablePaginationConfig;
}

export function usePagination<T>({
    fetchFn,
    errorMessage = "Error al cargar datos",
    initialParams,
    initialPagination,
}: UsePaginationOptions<T>) {
    const [data, setData] = useState<T[]>([]);
    const [loading, setLoading] = useState(false);
    const [params, setParams] = useState<PaginationRequestDTO>(initialParams || { page: 1, pageSize: 7 });

    const [pagination, setPagination] = useState<TablePaginationConfig>({
        pageSize: initialParams?.pageSize || 7,
        current: initialParams?.page || 1,
        total: initialPagination?.total || 0,
        pageSizeOptions: initialPagination?.pageSizeOptions || ["7", "10", "20", "50", "100"],
        showSizeChanger: initialPagination?.showSizeChanger || true,
        showTotal: initialPagination?.showTotal || ((total) => `Total: ${total} registros`),
    });

    const loadData = useCallback(async (customParams?: PaginationRequestDTO) => {
        setLoading(true);
        try {
            const paramsToUse = customParams || params;
        if (customParams) setParams(customParams);
            const { success, data: responseData, error } = await fetchFn(paramsToUse);

            if (success && responseData) {
                setData(responseData.data);
                setPagination((prev) => ({
                    ...prev,
                    total: responseData.pagination.total,
                    current: responseData.pagination.page,
                }));
            } else {
                Message.error({ content: error || errorMessage });
            }
        } catch (error) {
            Message.error({
                content: error instanceof Error ? error.message : errorMessage,
            });
        } finally {
            setLoading(false);
        }
    }, [fetchFn, params, errorMessage]);

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        const newParams = {
            ...params,
            page,
            pageSize,
        };
        setParams(newParams);
        setPagination((prev) => ({
            ...prev,
            current: page,
            pageSize: pageSize,
        }));
        loadData(newParams);
    }, [params, loadData]);


    useEffect(() => {
        loadData();
    }, [loadData]);

    return {
        data,
        loading,
        pagination: {
            ...pagination,
            onChange: handlePaginationChange,
            onShowSizeChange: handlePaginationChange,
        },
        loadData,
        params,
        setParams,
    };
}
